import { useState, useEffect, useRef } from "react";
import { Box, Button, Card, CardContent, Typography, Toolbar, FormControl, InputLabel, Select, MenuItem, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from 'react-to-print';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useAuth from '../../hooks/useAuth';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';

const WeeklyVisitFormPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation("global");
    const axiosPrivate = useAxiosPrivate();
    const { auth } = useAuth();
    const BASE_PATH = process.env.REACT_APP_BASE_PATH || '';
    
    const [elderly, setElderly] = useState([]);
    const [loading, setLoading] = useState(true);
    const [weekOffset, setWeekOffset] = useState(0); // 0 = settimana corrente, 1 = prossima, -1 = precedente
    const [showPreview, setShowPreview] = useState(false);
    const [companyName, setCompanyName] = useState('CASA FRANCESCANA');
    const printRef = useRef();

    // Carica i dati del profilo per ottenere la ragione sociale
    useEffect(() => {
        if (!auth?.accessToken) return;
        
        const fetchCompanyName = async () => {
            try {
                const response = await axiosPrivate.get('/auth/profile');
                if (response.data.company_name) {
                    setCompanyName(response.data.company_name);
                }
            } catch (error) {
                console.error('Errore caricamento ragione sociale:', error);
            }
        };
        
        fetchCompanyName();
    }, [auth?.accessToken, axiosPrivate]);

    // Carica la lista degli anziani
    useEffect(() => {
        if (!auth?.accessToken) return;
        
        const fetchElderly = async () => {
            try {
                setLoading(true);
                const response = await axiosPrivate.get('/elderly');
                const elderlyList = Array.isArray(response.data) ? response.data : [];
                // Ordina per cognome e nome
                const sorted = elderlyList.sort((a, b) => {
                    const nameA = `${a.surname} ${a.name}`.toLowerCase();
                    const nameB = `${b.surname} ${b.name}`.toLowerCase();
                    return nameA.localeCompare(nameB);
                });
                setElderly(sorted);
            } catch (error) {
                console.error('Errore nel caricamento anziani:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchElderly();
    }, [auth?.accessToken, axiosPrivate]);

    // Calcola le date della settimana (solo 6 giorni: Lun-Sab)
    const getWeekDates = (offset = 0) => {
        const today = new Date();
        const currentDay = today.getDay();
        const diff = currentDay === 0 ? -6 : 1 - currentDay; // Lunedì come primo giorno
        
        const monday = new Date(today);
        monday.setDate(today.getDate() + diff + (offset * 7));
        monday.setHours(0, 0, 0, 0);
        
        const dates = [];
        for (let i = 0; i < 6; i++) { // Solo 6 giorni (Lun-Sab)
            const date = new Date(monday);
            date.setDate(monday.getDate() + i);
            dates.push(date);
        }
        
        return dates;
    };

    const weekDates = getWeekDates(weekOffset);
    const mondayDate = weekDates[0];
    const saturdayDate = weekDates[5];

    // Funzione di stampa
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: `Visite_Parenti_${mondayDate.toLocaleDateString('it-IT').replace(/\//g, '-')}`,
        pageStyle: `
            @page {
                size: A4 landscape;
                margin: 10mm;
            }
            @media print {
                body { 
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
            }
        `
    });

    const formatDate = (date) => {
        return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
    };

    const dayNames = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];

    // Calcola quanti anziani per pagina (circa 12-15 per pagina A4 landscape)
    const elderlyPerPage = 12;
    const totalPages = Math.ceil(elderly.length / elderlyPerPage);

    return (
        <Box
            component="main"
            sx={{
                flexGrow: 1,
                p: 3,
                width: { sm: `calc(100% - 240px)` },
                ml: { sm: `240px` },
            }}
        >
            <Toolbar />

            {/* Header con controlli */}
            <Box sx={{ mb: 3 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(`${BASE_PATH}/documents`)}
                    sx={{ mb: 2 }}
                >
                    Torna ai Documenti
                </Button>

                <Typography variant="h4" gutterBottom>
                    Scheda Visite Settimanali
                </Typography>

                <Card sx={{ mt: 2 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <FormControl sx={{ minWidth: 200 }}>
                                <InputLabel>Seleziona Settimana</InputLabel>
                                <Select
                                    value={weekOffset}
                                    onChange={(e) => setWeekOffset(e.target.value)}
                                    label="Seleziona Settimana"
                                >
                                    <MenuItem value={-1}>Settimana Precedente</MenuItem>
                                    <MenuItem value={0}>Settimana Corrente</MenuItem>
                                    <MenuItem value={1}>Settimana Prossima</MenuItem>
                                </Select>
                            </FormControl>

                            <Typography variant="body1" sx={{ flex: 1 }}>
                                <strong>Periodo:</strong> {mondayDate.toLocaleDateString('it-IT')} - {saturdayDate.toLocaleDateString('it-IT')}
                            </Typography>

                            <Button
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                onClick={() => setShowPreview(!showPreview)}
                            >
                                {showPreview ? 'Nascondi' : 'Mostra'} Anteprima
                            </Button>

                            <Button
                                variant="contained"
                                startIcon={<PrintIcon />}
                                onClick={handlePrint}
                                disabled={loading || elderly.length === 0}
                            >
                                Stampa PDF
                            </Button>
                        </Box>

                        {!loading && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                📄 {elderly.length} ospiti • {totalPages} {totalPages === 1 ? 'pagina' : 'pagine'}
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            </Box>

            {/* Loading */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Anteprima/Stampa */}
            {!loading && showPreview && (
                <Box sx={{ 
                    mt: 3, 
                    border: '1px solid #ccc', 
                    borderRadius: 1,
                    backgroundColor: '#f5f5f5',
                    p: 2,
                    overflow: 'auto'
                }}>
                    <div ref={printRef}>
                        {Array.from({ length: totalPages }).map((_, pageIndex) => {
                            const startIndex = pageIndex * elderlyPerPage;
                            const endIndex = Math.min(startIndex + elderlyPerPage, elderly.length);
                            const pageElderly = elderly.slice(startIndex, endIndex);

                            return (
                                <div 
                                    key={pageIndex}
                                    style={{
                                        width: '297mm',
                                        height: '210mm',
                                        backgroundColor: 'white',
                                        padding: '10mm',
                                        marginBottom: pageIndex < totalPages - 1 ? '20px' : '0',
                                        boxSizing: 'border-box',
                                        pageBreakAfter: pageIndex < totalPages - 1 ? 'always' : 'auto',
                                        fontFamily: 'Arial, sans-serif'
                                    }}
                                >
                                    {/* Intestazione */}
                                    <div style={{ marginBottom: '15px', borderBottom: '2px solid #000', paddingBottom: '10px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <h1 style={{ margin: 0, fontSize: '20px', color: '#000', fontWeight: 'bold' }}>{companyName} – VISITE PARENTI</h1>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#000' }}>
                                                    Settimana dal {mondayDate.toLocaleDateString('it-IT')} al {saturdayDate.toLocaleDateString('it-IT')}
                                                </div>
                                                <div style={{ fontSize: '11px', color: '#666', marginTop: '5px' }}>
                                                    Stampato il {new Date().toLocaleDateString('it-IT')} • Pagina {pageIndex + 1} di {totalPages}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Tabella */}
                                    <table style={{ 
                                        width: '100%', 
                                        borderCollapse: 'collapse',
                                        fontSize: '10px', color: '#000'
                                    }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f0f0f0' }}>
                                                <th style={{ 
                                                    border: '1px solid #000', 
                                                    padding: '8px', 
                                                    textAlign: 'left',
                                                    width: '180px',
                                                    fontWeight: 'bold', color: '#000'
                                                }}>
                                                    CONVIVENTE
                                                </th>
                                                {dayNames.map((day, i) => (
                                                    <th key={i} style={{ 
                                                        border: '1px solid #000', 
                                                        padding: '8px', 
                                                        textAlign: 'left',
                                                        fontWeight: 'bold', color: '#000'
                                                    }}>
                                                        <div>{day}</div>
                                                        <div style={{ fontSize: '9px', fontWeight: 'normal' }}>
                                                            {formatDate(weekDates[i])}
                                                        </div>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pageElderly.map((elder, idx) => (
                                                <tr key={elder.elderly_id}>
                                                    <td style={{ 
                                                        border: '1px solid #000', 
                                                        padding: '8px',
                                                        fontWeight: '500',
                                                        color: '#000'
                                                    }}>
                                                        {elder.name} {elder.surname}
                                                    </td>
                                                    {dayNames.map((_, dayIdx) => (
                                                        <td key={dayIdx} style={{ 
                                                            border: '1px solid #000', 
                                                            padding: '2px 3px',
                                                            textAlign: 'left',
                                                            verticalAlign: 'middle'
                                                        }}>
                                                            <div style={{ color: '#000', fontSize: '9px' }}>
                                                                <div style={{ marginBottom: '2px' }}>ENTRATA: ________</div>
                                                                <div>USCITA: ________</div>
                                                            </div>
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {/* Note a piè di pagina */}
                                    <div style={{ 
                                        marginTop: '15px', 
                                        fontSize: '9px', 
                                        color: '#666',
                                        borderTop: '1px solid #ccc',
                                        paddingTop: '8px'
                                    }}>
                                        <strong>Note:</strong> Compilare manualmente gli orari di arrivo e partenza dei visitatori, e indicare il nome del visitatore
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Box>
            )}

            {/* Hidden print component */}
            {!showPreview && (
                <div style={{ display: 'none' }}>
                    <div ref={printRef}>
                        {Array.from({ length: totalPages }).map((_, pageIndex) => {
                            const startIndex = pageIndex * elderlyPerPage;
                            const endIndex = Math.min(startIndex + elderlyPerPage, elderly.length);
                            const pageElderly = elderly.slice(startIndex, endIndex);

                            return (
                                <div 
                                    key={pageIndex}
                                    style={{
                                        width: '297mm',
                                        height: '210mm',
                                        backgroundColor: 'white',
                                        padding: '10mm',
                                        marginBottom: pageIndex < totalPages - 1 ? '20px' : '0',
                                        boxSizing: 'border-box',
                                        pageBreakAfter: pageIndex < totalPages - 1 ? 'always' : 'auto',
                                        fontFamily: 'Arial, sans-serif'
                                    }}
                                >
                                    {/* Intestazione */}
                                    <div style={{ marginBottom: '15px', borderBottom: '2px solid #000', paddingBottom: '10px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <h1 style={{ margin: 0, fontSize: '20px', color: '#000', fontWeight: 'bold' }}>{companyName} – VISITE PARENTI</h1>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#000' }}>
                                                    Settimana dal {mondayDate.toLocaleDateString('it-IT')} al {saturdayDate.toLocaleDateString('it-IT')}
                                                </div>
                                                <div style={{ fontSize: '11px', color: '#666', marginTop: '5px' }}>
                                                    Stampato il {new Date().toLocaleDateString('it-IT')} • Pagina {pageIndex + 1} di {totalPages}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Tabella */}
                                    <table style={{ 
                                        width: '100%', 
                                        borderCollapse: 'collapse',
                                        fontSize: '10px', color: '#000'
                                    }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f0f0f0' }}>
                                                <th style={{ 
                                                    border: '1px solid #000', 
                                                    padding: '8px', 
                                                    textAlign: 'left',
                                                    width: '180px',
                                                    fontWeight: 'bold', color: '#000'
                                                }}>
                                                    CONVIVENTE
                                                </th>
                                                {dayNames.map((day, i) => (
                                                    <th key={i} style={{ 
                                                        border: '1px solid #000', 
                                                        padding: '8px', 
                                                        textAlign: 'left',
                                                        fontWeight: 'bold', color: '#000'
                                                    }}>
                                                        <div>{day}</div>
                                                        <div style={{ fontSize: '9px', fontWeight: 'normal' }}>
                                                            {formatDate(weekDates[i])}
                                                        </div>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pageElderly.map((elder, idx) => (
                                                <tr key={elder.elderly_id}>
                                                    <td style={{ 
                                                        border: '1px solid #000', 
                                                        padding: '8px',
                                                        fontWeight: '500',
                                                        color: '#000'
                                                    }}>
                                                        {elder.name} {elder.surname}
                                                    </td>
                                                    {dayNames.map((_, dayIdx) => (
                                                        <td key={dayIdx} style={{ 
                                                           
                                                            padding: '2px 3px',
                                                            textAlign: 'left',
                                                            verticalAlign: 'middle'
                                                        }}>
                                                            <div style={{ color: '#000', fontSize: '7.5px', marginBottom: '1px' }}>
                                                                Arr: <span style={{ padding: '0 4px', display: 'inline-block', marginRight: '4px' }}>__:__</span>
                                                                Part: <span style={{  padding: '0 4px', display: 'inline-block' }}>__:__</span>
                                                            </div>
                                                            <div style={{ fontSize: '7px', color: '#000' }}>
                                                                Vis: <span style={{  padding: '0 5px', display: 'inline-block' }}>________</span>
                                                            </div>
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {/* Note a piè di pagina */}
                                    <div style={{ 
                                        marginTop: '15px', 
                                        fontSize: '9px', 
                                        color: '#666',
                                        borderTop: '1px solid #ccc',
                                        paddingTop: '8px'
                                    }}>
                                        <strong>Note:</strong> Compilare manualmente gli orari di arrivo e partenza dei visitatori, e indicare il nome del visitatore
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </Box>
    );
};

export default WeeklyVisitFormPage;
